import { useState } from 'react';
import { adminApi } from '../../services/api';
import { DeviceType, type Device, type CreateDeviceRequest, type UpdateDeviceRequest } from '../../types/admin';

interface DeviceModalProps {
  userId: string;
  device: Device | null;
  onClose: (updated: boolean) => void;
}

const DeviceTypeOptions = [
  { value: DeviceType.Smartphone, label: 'Smartphone' },
  { value: DeviceType.Tablet, label: 'Tablet' },
  { value: DeviceType.Laptop, label: 'Laptop' },
  { value: DeviceType.Desktop, label: 'Desktop' },
  { value: DeviceType.Television, label: 'Television' },
  { value: DeviceType.StreamingDevice, label: 'Streaming Device' },
  { value: DeviceType.Router, label: 'Router' },
  { value: DeviceType.Modem, label: 'Modem' },
  { value: DeviceType.SmartSpeaker, label: 'Smart Speaker' },
  { value: DeviceType.Printer, label: 'Printer' },
  { value: DeviceType.Camera, label: 'Camera' },
  { value: DeviceType.SmartWatch, label: 'Smart Watch' },
  { value: DeviceType.GamingConsole, label: 'Gaming Console' },
  { value: DeviceType.SmartHome, label: 'Smart Home' },
  { value: DeviceType.Other, label: 'Other' }
];

export default function DeviceModal({ userId, device, onClose }: DeviceModalProps) {
  const [formData, setFormData] = useState({
    name: device?.name || '',
    type: device?.type ?? DeviceType.Smartphone,
    brand: device?.brand || '',
    model: device?.model || '',
    notes: device?.notes || '',
    imageUrl: device?.imageUrl || '',
    isActive: device?.isActive ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Device name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (device) {
        // Update existing device
        const updateData: UpdateDeviceRequest = {
          name: formData.name.trim(),
          type: formData.type,
          brand: formData.brand.trim() || undefined,
          model: formData.model.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          imageUrl: formData.imageUrl.trim() || undefined,
          isActive: formData.isActive
        };
        await adminApi.updateDevice(device.id, updateData);
      } else {
        // Create new device
        const createData: CreateDeviceRequest = {
          name: formData.name.trim(),
          type: formData.type,
          brand: formData.brand.trim() || undefined,
          model: formData.model.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          imageUrl: formData.imageUrl.trim() || undefined
        };
        await adminApi.createDevice(userId, createData);
      }

      onClose(true);
    } catch (err) {
      console.error('Error saving device:', err);
      setError('Failed to save device');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{device ? 'Edit Device' : 'Add Device'}</h2>
          <button onClick={() => onClose(false)} className="btn-close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="device-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Device Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Living Room TV"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Device Type *</label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: Number(e.target.value) })}
              required
            >
              {DeviceTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="brand">Brand</label>
              <input
                id="brand"
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                placeholder="e.g., Samsung"
              />
            </div>

            <div className="form-group">
              <label htmlFor="model">Model</label>
              <input
                id="model"
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="e.g., Galaxy S21"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional information about this device..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              id="imageUrl"
              type="url"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              placeholder="https://..."
            />
          </div>

          {device && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span>Active (include in AI context)</span>
              </label>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={() => onClose(false)} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : device ? 'Update Device' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
